import { useContext, useEffect, useState } from "preact/hooks";
import { loadSession, logError, StateCtx } from "./app.tsx";
import { Footer } from "./footer.tsx";
import { Header } from "./header.tsx";
import { signin } from "./webauthn.tsx";
import { decoder, encoder } from "./utils.ts";

// The IndieAuth issuer, matching the backend metadata document.
const ISSUER = `https://${location.host}/`;

interface AuthRequest {
  clientId: string;
  redirectUri: string;
  me: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  scope: string;
  responseType: string;
}

function parseRequest(): AuthRequest | null {
  const q = new URLSearchParams(location.search);
  const clientId = q.get("client_id") ?? "";
  const redirectUri = q.get("redirect_uri") ?? "";
  if (!clientId || !redirectUri) return null;
  return {
    clientId,
    redirectUri,
    me: q.get("me") ?? "",
    state: q.get("state") ?? "",
    codeChallenge: q.get("code_challenge") ?? "",
    codeChallengeMethod: q.get("code_challenge_method") ?? "",
    scope: q.get("scope") ?? "",
    responseType: q.get("response_type") ?? "code",
  };
}

function hostOf(value: string): string {
  try {
    return new URL(value).host;
  } catch {
    return value;
  }
}

// The xmit site backing the requested identity, as resolved by the backend.
interface ResolvedSite {
  siteID: number;
  name: string;
  me: string;
  owned: boolean;
}

async function resolveMe(me: string): Promise<ResolvedSite> {
  const resp = await fetch(
    `/api/web/indieauth/resolve?me=${encodeURIComponent(me)}`,
  );
  if (!resp.ok) {
    const body = (await resp.text()).trim();
    throw new Error(body || "This address isn't an xmit site.");
  }
  const m = decoder.decode(
    new Uint8Array(await resp.arrayBuffer()),
  ) as Map<number, any>;
  return { siteID: m.get(1), name: m.get(2), me: m.get(3), owned: m.get(4) };
}

// mintCode asks the backend for a single-use authorization code. The session
// cookie proves the user owns the site.
async function mintCode(site: ResolvedSite, req: AuthRequest): Promise<string> {
  const resp = await fetch("/api/web/indieauth/mint", {
    method: "POST",
    body: encoder.encode(
      new Map<number, any>([
        [1, site.me],
        [2, req.clientId],
        [3, req.redirectUri],
        [4, req.codeChallenge],
        [5, req.codeChallengeMethod],
        [6, req.scope],
      ]),
    ),
  });
  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(body.trim() || `${resp.status}`);
  }
  const m = decoder.decode(
    new Uint8Array(await resp.arrayBuffer()),
  ) as Map<number, any>;
  return m.get(1);
}

type Phase = "loading" | "ready" | "submitting" | "error";

export function IndieAuthConsent() {
  const state = useContext(StateCtx).value;
  const session = loadSession(state);
  const [req] = useState(parseRequest);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState("");
  const [site, setSite] = useState<ResolvedSite | null>(null);

  useEffect(() => {
    if (!req) {
      setError("This sign-in request is missing required details.");
      setPhase("error");
      return;
    }
    if (req.responseType !== "code") {
      setError("This sign-in request isn't supported.");
      setPhase("error");
      return;
    }
    let cancelled = false;
    resolveMe(req.me)
      .then((r) => {
        if (cancelled) return;
        setSite(r);
        setPhase("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const redirectBack = (params: Record<string, string>) => {
    const url = new URL(req!.redirectUri);
    for (const [k, v] of Object.entries(params))
      if (v) url.searchParams.set(k, v);
    if (req!.state) url.searchParams.set("state", req!.state);
    location.assign(url.toString());
  };

  // After sign-in the cookie changes, so re-resolve to learn whether the new
  // account owns the site.
  const signInAndResolve = () => {
    signin()
      .then(() => resolveMe(req!.me))
      .then(setSite)
      .catch(logError);
  };

  const approve = async (e: Event) => {
    e.preventDefault();
    if (!req || !site) return;
    setError("");
    setPhase("submitting");
    try {
      const code = await mintCode(site, req);
      redirectBack({ code, iss: ISSUER });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("ready");
    }
  };

  const deny = () => {
    if (req) redirectBack({ error: "access_denied" });
  };

  if (phase === "error") {
    return (
      <div class="with-header">
        <Header session={session} />
        <main>
          <h1>🛑 Can't sign in</h1>
          <section>
            <p>{error}</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (phase === "loading" || !site) {
    return (
      <div class="with-header">
        <Header session={session} />
        <main>
          <p>
            <span class="spinner">⟳</span> Checking this sign-in request…
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const clientHost = hostOf(req!.clientId);
  const identity = site.me.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const scopes = req!.scope.split(/\s+/).filter(Boolean);
  const signedIn = session?.uid !== undefined;

  return (
    <div class="with-header">
      <Header session={session} />
      <main>
        <h1>{scopes.length ? "🔑 Authorize" : "🚪 Sign in"}</h1>
        <section>
          {scopes.length ? (
            <>
              <p>
                <strong>{clientHost}</strong> wants to access{" "}
                <strong>{identity}</strong> with these permissions:
              </p>
              <ul>
                {scopes.map((scope) => (
                  <li key={scope}>{scope}</li>
                ))}
              </ul>
            </>
          ) : (
            <p>
              <strong>{clientHost}</strong> wants to confirm you own{" "}
              <strong>{identity}</strong>.
            </p>
          )}
          {!signedIn ? (
            <p>
              <button type="button" onClick={signInAndResolve}>
                🚪 Sign in to continue
              </button>
            </p>
          ) : !site.owned ? (
            <p>
              This account doesn't own <strong>{identity}</strong>.{" "}
              <button type="button" onClick={signInAndResolve}>
                🚪 Sign in with another passkey
              </button>
            </p>
          ) : (
            <form onSubmit={approve}>
              <p>
                Continue as <strong>{identity}</strong>
                {site.name ? ` (${site.name})` : ""}?
              </p>
              {error && <p class="red">{error}</p>}
              <p>
                <button type="submit" disabled={phase === "submitting"}>
                  {phase === "submitting"
                    ? "Signing in…"
                    : scopes.length
                      ? "Allow access"
                      : "Continue"}
                </button>{" "}
                <button
                  type="button"
                  onClick={deny}
                  disabled={phase === "submitting"}
                >
                  Cancel
                </button>
              </p>
            </form>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
